'use client';

import useSWR, { type SWRConfiguration } from 'swr';
import customFetch from '@/lib/utils/fetch';

export interface TopHolderWallet {
  wallet: string;
  name: string | null;
  pseudonym: string | null;
  amount: number;
  percentage: number | null;
}

export interface TopHolderToken {
  token: string;
  outcome: 'yes' | 'no';
  outcome_label: string;
  total_volume_usdc: number | null;
  top10_percentage: number | null;
  holders: TopHolderWallet[];
}

export interface MarketTopHolders {
  condition_id: string;
  market_question: string | null;
  tokens: TopHolderToken[];
}

export type TopHolderData = MarketTopHolders[];

export interface TopHolderResponse {
  success: boolean;
  data: TopHolderData;
  message?: string;
  error?: string;
}

export type FetchTopHolderParams =
  | {
      conditionId: string;
      conditionIds?: string | string[] | null;
    }
  | {
      conditionId?: string | null;
      conditionIds: string | string[];
    };

export type UseTopHolderOptions = FetchTopHolderParams & {
  enabled?: boolean;
  swr?: SWRConfiguration<TopHolderResponse, Error>;
};

export async function fetchTopHolder(params: FetchTopHolderParams) {
  const conditionIds = normalizeConditionIds(params);

  if (conditionIds.length === 0) {
    throw new Error('conditionId or conditionIds is required');
  }

  const searchParams = new URLSearchParams();

  if (conditionIds.length === 1) {
    searchParams.set('condition_id', conditionIds[0]);
  } else {
    searchParams.set('condition_ids', conditionIds.join(','));
  }

  const response = await customFetch(`/api/market/top-holder?${searchParams.toString()}`);

  if (!(response instanceof Response)) {
    throw response instanceof Error ? response : new Error('Failed to fetch top holder');
  }

  const result = (await response.json()) as TopHolderResponse;

  if (!result.success) {
    throw new Error(result.message || result.error || 'Failed to fetch top holder');
  }

  return result;
}

export function useTopHolder(options: UseTopHolderOptions) {
  const conditionIds = normalizeConditionIds(options);
  const enabled = options.enabled ?? true;
  const conditionIdsKey = conditionIds.join(',');
  const swrKey = enabled && conditionIds.length > 0 ? ['top-holder', conditionIdsKey] : null;

  const { data, error, isLoading, isValidating, mutate } = useSWR<TopHolderResponse, Error>(
    swrKey,
    () => fetchTopHolder({ conditionIds }),
    {
      revalidateOnFocus: false,
      keepPreviousData: true,
      ...options.swr,
    },
  );

  const topHolders = Array.isArray(data?.data) ? data.data : [];
  const tokens = topHolders.flatMap((holder) => holder.tokens);

  return {
    data: data ?? null,
    holders: topHolders,
    topHolders,
    tokens,
    holderData: topHolders,
    conditionIds,
    error,
    isLoading,
    isValidating,
    refresh: mutate,
  };
}

function normalizeConditionIds({ conditionId, conditionIds }: FetchTopHolderParams) {
  const ids = [
    conditionId,
    ...(Array.isArray(conditionIds) ? conditionIds : (conditionIds?.split(',') ?? [])),
  ];

  return [...new Set(ids.map((id) => id?.trim()).filter(Boolean))] as string[];
}

export default useTopHolder;
